import React from 'react';
import CustomFieldsLandingPage from './CustomFieldsLandingPage';
import { generateComponentDocs, generateStoryDocs } from '../../../utils/documentationUtils';

export default {
  title: 'Pages/Admin/CustomFields',
  component: CustomFieldsLandingPage,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      `\n**CustomFieldsLandingPage** is the primary interface for managing custom fields within an organization.
        \n\n### _***Key Functionalities:***_
        \n- **Add and manage** custom fields for different entities (**Sites, Visits, Contacts**).
        \n- **Navigate** between different sections using **tabs**.
        \n- **Display** a list of fields specific to an organization.
        \n\n### **How it works?**
        \n- Users can switch between **Sites, Visits, and Contacts** tabs.
        \n- If fields exist, they are displayed within the selected tab.
        \n- If no fields are available, a message _"Add your first custom field."_ is shown.
        \n- Clicking the **"Custom Field"** button allows users to add a new field.\n\n`,
      // When & how to use it?
      `\n\n### _**Usage**_
        \nThis page is used by **manages** and **planners** to manage additional metadata fields within the system.
        \n\n### _**Key Features**_

        \n**Tabs Navigation**: 
        \n- Uses the \`Tabs\` component to switch between different entities.

        \n\n**Dynamic Content**: 
        \n- If data exists, it displays the list of fields.
        \n- If no data is available, it prompts the user with a message: _"Add your first custom field."_.

        \n\n**Custom Fields Management**:
        \n- Users can click the **"Custom Field"** button to add a new field.`,
      // Current Usage
      `\nOnly managers and planners can access the web application. 
        \n\n###_**Access**_
        \n- Accessible on Vamos web for **managers** and **planners**.
        \n\n###_**Roles**_
        \n\n- Manager:
        \nFull access: can create, view, edit, and delete custom fields for the organization.
        \n\n- Planners:
        \nPartial access: can only view custom fields for the organization.
        \n- User:
        \nNo access: can not access to the Vamos web app and cannot access the Custom Fields page or create/modify custom fields within the web app. (They are restricted to mobile app usage only.)
        \n\n>Note: The access rights described here may not have been fully implemented at the time this documentation was created. The description of roles reflects the intended behavior for access rights as outlined above.\n\n`,
      // Related Components
      '\n- **[Breadcrumbs](http://localhost:6006/?path=/docs/components-breadcrumbs--docs)**: Provides navigation context.',
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    organizationId: {
      control: { type: 'number' },
      description: 'The organization ID used to fetch custom fields data.',
    },
  },
};

const Template = () => <CustomFieldsLandingPage />;

export const LandingPage = Template.bind({});

LandingPage.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used when managing custom fields for different sections (Sites, Visits, Contacts). It provides a structured interface for manages to add and view fields.',
  // Where is it currently used in the product?
  '\nUsers can navigate to this page via: \n- **Dashboard → Admin → Custom Fields**.',
);
